import React from 'react';
import { Stack } from '@fiverr-private/layout_components';
import { Box, tokens } from '@fiverr-private/theme';
import { Text } from '@fiverr-private/typography';
import { Tooltip } from '@fiverr-private/tooltip';
import type { ProgressLineProps } from './types';
import { getProgressColors, getProgressPercent } from './helpers';
import { LINE_HEIGHT, MAX_PERCENT } from './constants';

const ProgressLine = ({
  percent = 0,
  threshold,
  startLabel,
  endLabel,
  progressTooltip,
  appendTooltipToBody = false,
  progressColor = 'default',
  trackColor = 'default',
  borderRadius = 'circle',
  ...restProps
}: ProgressLineProps) => {
  const progressWidth = getProgressPercent(percent);
  const { progressBackgroundColor, trackBackgroundColor } = getProgressColors({ progressColor, trackColor });

  return (
    <Stack
      alignItems="center"
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      {...restProps}
    >
      {startLabel && (
        <Text size="b_sm" fontWeight="bold" color="bodySecondary">
          {startLabel}
        </Text>
      )}
      <Box
        height={LINE_HEIGHT}
        flex="1"
        position="relative"
        backgroundColor={trackBackgroundColor}
        borderRadius={borderRadius}
      >
        {threshold && (
          <Box height={LINE_HEIGHT} position="absolute" left={`${threshold}%`} width={2} backgroundColor="grey_1000" />
        )}
        <Box
          height={LINE_HEIGHT}
          width={`${progressWidth}%`}
          backgroundColor={progressBackgroundColor}
          transition={`${tokens.animationDuration.moderate1} ${tokens.animationTiming.linear} width`}
          {...(progressWidth === MAX_PERCENT
            ? {
                borderRadius,
              }
            : { borderTopLeftRadius: borderRadius, borderBottomLeftRadius: borderRadius })}
        />
        {progressTooltip && (
          <Box position="absolute" left={`${progressWidth}%`} top={0}>
            <Tooltip isOpen content={progressTooltip} appendToBody={appendTooltipToBody}>
              <Box as="span" height={LINE_HEIGHT} width={0.1} />
            </Tooltip>
          </Box>
        )}
      </Box>
      {endLabel && (
        <Text size="b_sm" fontWeight="bold" color="bodySecondary">
          {endLabel}
        </Text>
      )}
    </Stack>
  );
};

ProgressLine.displayName = 'ProgressLine';

export default ProgressLine;
